'use client';
import { useAddOrUpdatePost } from '@/actions/feed.actions';
import PostViewersSelector from '@/components/PostViewerSelector/PostViewersSelector';
import PostViewersSelectorSchema from '@/components/PostViewerSelector/postViewerSelector.validator';
import Avatar from '@/components/next-ui/Avatar';
import Dialog from '@/components/next-ui/Dialog';
import Typography from '@/components/next-ui/Typography';
import { ProfileTypes, TargetViewers } from '@/constants/profileType.constant';
import {
  useIsCommunityAdmin,
  useIsCompanyAdmin,
  useIsNonProfitUser,
  useProfileType,
} from '@/hooks/useUser';
import { uploadToCloudinary } from '@/utils/cloudinary';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@ui/Button';
import produce from 'immer';
import { isArray, isEmpty, isString, isUndefined, trim } from 'lodash-es';
import Image from 'next/image';
import React, { use, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { IoMdVideocam } from 'react-icons/io';
import { MdPhoto } from 'react-icons/md';
import * as yup from 'yup';
import ImageCarousel from '../ImageCarousel';
import AddNonProfitToPost from './AddNonProfitToPost';
import AttachPostMedia from './AttachPostMedia';
import CreatePostForm from './CreatePostForm';
import useUserProfile from '@/hooks/useUserProfile';
import { cx } from 'cva';
import ActivitySuccessDialog from '@/components/ActivitySuccessDialog';
import { PublishToPublicPage } from '@/components/PublishToPublicPage';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import PostNotify from '../PostNotify';
import PostType from '@/constants/postType.constants';
import OrgType from '@/constants/orgType.constant';

const schema = yup
  .object()
  .shape({
    message: yup
      .string()
      .max(5000, 'Message is too long, max 5000 characters allowed'),
  })
  .concat(PostViewersSelectorSchema);

function CreatePostDialog(props) {
  const {
    open,
    post,
    onClose,
    message = null,
    supportedNonProfit = null,
  } = props;

  const profile = useUserProfile();
  const isNonProfitUser = useIsNonProfitUser();
  const isAdmin = useIsCommunityAdmin();
  const isCompanyAdmin = useIsCompanyAdmin();

  const [postPayload, setPostPayload] = React.useState({});
  const [step, setStep] = React.useState('form');
  const [isSubmited, setIsSubmited] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const [selectedNonProfit, setSelectedNonProfit] =
    React.useState(supportedNonProfit);
  const [reward, setReward] = React.useState(null);
  const [createdPost, setCreatedPost] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const profileType = useProfileType();

  const isLessThanSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('sm'));

  const defaultFormValues = post
    ? {
        postAs: post.postAs,
        targetViewers: post.targetViewers,
        targetOrgs: post.targetOrgs,
        message: post.message,
        isFeatured: post.isFeatured,
      }
    : {
        postAs: profileType,
        targetViewers:
          profileType === ProfileTypes.Organization
            ? TargetViewers.All
            : TargetViewers.MyOrg,
        targetOrgs: [],
        message: message || '',
        isFeatured: false,
      };

  const formMethods = useForm({
    defaultValues: defaultFormValues,
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    formState: { isValid },
    trigger,
    getValues,
    reset,
  } = formMethods;

  const { mutateAsync: addOrUpdatePost, isError } = useAddOrUpdatePost();

  const getButtonLabel = () => {
    if (isEditMode) {
      return 'Save';
    }
    return isAdmin ? 'Next' : 'Post';
  };

  const handleSetPayload = () => {
    const nonProfit =
      supportedNonProfit ||
      post?.renderProps?.nonProfit ||
      profile.activeCommunity?.nonProfit;

    setSelectedNonProfit(supportedNonProfit || nonProfit || null);
    // Reset post payload

    if (supportedNonProfit) {
      setPostPayload({
        message: message || post?.sourcePostData?.message || '',
        postAs: post?.postAs || profileType,
        targetViewers: post?.targetViewers || getValues('targetViewers'),
        targetOrgs: post?.targetOrgs || [],
        isFeatured: post?.isFeatured || false,
        nonProfitId: supportedNonProfit?.id,
        attachmentUrl: supportedNonProfit?.logo_url
          ? [supportedNonProfit?.logo_url]
          : [],
        attachmentVideos: post?.sourcePostData?.attachmentVideos || [],
        attachmentType: supportedNonProfit?.logo_url ? 'image' : null,
      });
    } else {
      setPostPayload({
        message: message || post?.sourcePostData?.message || '',
        postAs: post?.postAs || profileType,
        targetViewers: post?.targetViewers || getValues('targetViewers'),
        targetOrgs: post?.targetOrgs || [],
        isFeatured: post?.isFeatured || false,
        nonProfitId: post?.nonProfitId || nonProfit?.id || null,
        attachmentUrl: post?.sourcePostData?.attachmentUrl || [],
        attachmentVideos: post?.sourcePostData?.attachmentVideos || [],
        attachmentType: post?.sourcePostData?.attachmentType || null,
      });
    }
  };

  useEffect(() => {
    if (open) {
      handleSetPayload();
    }
  }, [open, post, profileType]);

  useEffect(() => {
    if (isSubmited && !isLoading && !isError) {
      // handleClose(); // TODO: temporary - check - shyam
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmited, isLoading, isError]);

  useEffect(() => {
    handleSetPayload();
  }, [message, supportedNonProfit]);

  // derived state
  const isEditMode = !!post;

  const isFormValid =
    isValid &&
    (!isEmpty(trim(postPayload?.message)) ||
      !isEmpty(postPayload?.attachmentUrl) ||
      !isEmpty(postPayload?.attachmentVideos));

  const handleClose = () => {
    setIsSubmited(false);
    setStep('form');
    setSelectedNonProfit(null);
    reset(defaultFormValues);
    onClose();
  };

  const handleNonProfitSelection = (nonProfit) => {
    setSelectedNonProfit(nonProfit);
    setPostPayload({
      ...postPayload,
      nonProfitId: nonProfit?.id || null,
    });
    setStep('form');
  };

  const handleImageSelection = (images) => {
    setPostPayload(
      produce(postPayload, (draft) => {
        if (!isUndefined(images)) {
          draft.attachmentUrl = images;
          draft.attachmentType = 'image';
          draft.attachmentVideos = [];
        }
      }),
    );
    setStep('form');
  };

  const handleVideoSelection = (videos) => {
    setPostPayload(
      produce(postPayload, (draft) => {
        if (!isUndefined(videos)) {
          draft.attachmentVideos = videos || [];
          draft.attachmentType = 'video';
          draft.attachmentUrl = [];
        }
      }),
    );
    setStep('form');
  };

  const handleFormSubmit = () => {
    if (isFormValid) {
      if (step === 'form') {
        if (isEditMode || !isAdmin) {
          handleSubmit();
        } else {
          setStep('publish');
        }
      } else if (step === 'publish') {
        handleSubmit();
      }
    }
  };

  const handleSubmit = async () => {
    if (isFormValid) {
      const { postAs, targetViewers, targetOrgs, isFeatured } = getValues();
      const formData = new FormData();
      const {
        attachmentUrl: selectedImages,
        attachmentVideos: selectedVideos,
        ...rest
      } = postPayload;

      setIsLoading(true);
      setIsSubmited(true);
      const payload = {
        ...rest,
        isFeatured,
        postAs,
        targetViewers,
        targetOrgs,
        attachmentType: post?.sourcePostData?.attachmentType || 'image',
      };

      if (!isEditMode) {
        if (selectedImages && selectedImages.length > 0) {
          selectedImages.forEach((image) => {
            if (isString(image)) {
              if (!payload.attachmentUrl) {
                payload.attachmentUrl = [];
              }
              payload.attachmentUrl.push(image);
            } else {
              formData.append('attachmentUrl', image);
            }
          });
        } else {
          // delete the attachment if there is no image selected
          payload.attachmentUrl = null;
        }

        // for videos
        if (
          selectedVideos &&
          isArray(selectedVideos) &&
          selectedVideos.length > 0
        ) {
          let videos = [];
          if (selectedVideos[0].type) {
            try {
              const uploadedVideos = await Promise.all(
                uploadToCloudinary(selectedVideos),
              );
              videos = uploadedVideos;
            } catch (error) {
              console.log(error);
              return;
              // TODO Handle error
            }
          } else {
            videos = selectedVideos.map((video) => video.url);
          }
          payload.attachmentVideos = videos;
          payload.attachmentType = 'video';
        }
      }
      formData.append('data', JSON.stringify(payload));
      const data = await addOrUpdatePost({ formData, postId: post?.id });
      setReward(data?.reward);
      setCreatedPost(data?.post);
      setIsLoading(false);
      setStep('success');
    } else {
      setShowError(true);
      trigger();
    }
  };

  const getDialogTitle = () => {
    switch (step) {
      case 'form':
        return isEditMode ? 'Edit Post' : 'Create Post';
      case 'select-npo':
        return 'Tag Non Profit';
      case 'select-image':
        return 'Select Images';
      case 'select-video':
        return 'Select Videos';
      default:
        return '';
    }
  };

  const handleShowNonProfitSelection = () => {
    if (!isNonProfitUser && !isEditMode) {
      setStep('select-npo');
    }
  };

  if (!open || !postPayload) {
    return null;
  }

  return (
    <Dialog
      isOpen={open}
      maxWidth="lg"
      title={getDialogTitle()}
      onClose={() => handleClose()}>
      {step === 'form' && (
        <div className="flex flex-1 flex-col gap-4">
          <FormProvider {...formMethods}>
            <PostViewersSelector disabled={!!post} post={post} />
          </FormProvider>

          {/* <div>
            {isCompanyAdmin ? (
              <PostNotify
                postType={PostType.UserPost}
                orgId={profile?.activeCommunity.id}
                orgType={profile?.activeCommunity?.orgType}
              />
            ) : (
              <PostNotify
                postType={PostType.UserPost}
                orgId={postPayload.nonProfitId || profile?.activeCommunity.id}
                orgType={profile?.activeCommunity?.orgType}
                // orgType={
                //   postPayload.nonProfitId ? OrgType.NonProfit : OrgType.Business
                // }
              />
            )}
          </div> */}

          <div
            // TODO: prevent bottom part of the dialog box from being cut in ANDROID devices
            className={`flex w-full flex-col gap-4 overflow-y-auto ${
              isLessThanSmallScreen ? 'flex-9-1-0 justify-center' : 'flex-1'
            }`}>
            <CreatePostForm
              message={postPayload.message}
              imageUrls={postPayload.attachmentUrl}
              videoUrls={postPayload.attachmentVideos}
              onChange={(msg) =>
                setPostPayload({
                  ...postPayload,
                  message: msg,
                })
              }
              error={
                !isFormValid &&
                isValid &&
                showError &&
                'Please add a message, image or video'
              }
            />

            {!isEmpty(postPayload.attachmentUrl) && (
              <div className="flex w-full items-center justify-start gap-4">
                <ImageCarousel
                  size="xs"
                  images={postPayload.attachmentUrl}
                  onChange={handleImageSelection}
                  disabled={isEditMode}
                />
              </div>
            )}
            {!isEmpty(postPayload.attachmentVideos) && (
              <div className="flex w-full items-center justify-start gap-4">
                <ImageCarousel
                  type="video"
                  images={postPayload.attachmentVideos}
                  onChange={handleVideoSelection}
                  disabled={isEditMode}
                />
              </div>
            )}
          </div>
          {(!isEditMode || selectedNonProfit) && (
            <div
              className={cx(
                'flex w-full items-center justify-start gap-2 rounded-md bg-primary-light-1 px-2 py-3 lg:gap-4',
                !isNonProfitUser && 'group',
              )}
              role={isNonProfitUser || isEditMode ? '' : 'button'}
              onClick={handleShowNonProfitSelection}>
              {selectedNonProfit ? (
                <>
                  <Avatar
                    src={selectedNonProfit.logo_url}
                    alt={selectedNonProfit.name}
                    size="sm"
                  />
                  <Typography
                    variant="h3"
                    className={cx(
                      'text-text-disabled',
                      !isNonProfitUser &&
                        !isEditMode &&
                        'group-hover:text-text-hyperlink',
                    )}>
                    Supporting {selectedNonProfit.name}
                  </Typography>
                </>
              ) : (
                <>
                  <Button variant="icon">
                    <Image
                      src="/assets/icons/fundraise-campaign.svg"
                      alt="Tag Non Profit"
                      width={24}
                      height={24}
                      className="h-6 w-6"
                    />
                  </Button>
                  <Typography
                    variant="h3"
                    className="text-text-disabled group-hover:text-text-hyperlink">
                    Click to tag a Non Profit of your choice
                  </Typography>
                </>
              )}
            </div>
          )}

          <div
            className={cx(
              'flex w-full items-center justify-start gap-4',
              isLessThanSmallScreen && 'mb-32',
            )}>
            {!isEditMode && (
              <>
                <Button
                  variant="icon"
                  className="bg-body-background"
                  onClick={() => setStep('select-image')}>
                  <MdPhoto className="h-6 w-6 text-text-disabled" />
                </Button>

                <Button
                  variant="icon"
                  className="bg-body-background"
                  onClick={() => setStep('select-video')}>
                  <IoMdVideocam className="h-6 w-6 text-text-disabled" />
                </Button>
              </>
            )}

            <div className="ml-auto">
              <Button
                color="primary"
                variant="filled"
                size="md"
                disabled={!isFormValid}
                loading={!!isLoading}
                onClick={() => handleFormSubmit()}>
                {getButtonLabel()}
              </Button>
            </div>
          </div>
        </div>
      )}

      {step === 'select-npo' && (
        <div className="flex flex-1 flex-col gap-4">
          <AddNonProfitToPost onChange={handleNonProfitSelection} />
        </div>
      )}

      {step === 'select-image' && (
        <AttachPostMedia
          type="image"
          onChange={handleImageSelection}
          assets={postPayload.attachmentUrl || []}
        />
      )}

      {step === 'select-video' && (
        <AttachPostMedia
          type="video"
          onChange={handleVideoSelection}
          assets={postPayload.attachmentVideos || []}
        />
      )}

      {step === 'publish' && (
        <div className="flex flex-1 flex-col gap-4">
          <div
            className={cx('flex-1', isLessThanSmallScreen && 'max-h-[300px]')}>
            <FormProvider {...formMethods}>
              <PublishToPublicPage />
            </FormProvider>
          </div>

          <div className="ml-auto">
            <Button
              color="primary"
              variant="filled"
              size="md"
              // TODO: also taking the case when the video is uploaded via cloudinary
              loading={!!isLoading}
              onClick={() => handleFormSubmit()}>
              Post
            </Button>
          </div>
        </div>
      )}

      {step === 'success' && (
        <ActivitySuccessDialog
          supportedNonProfit={selectedNonProfit}
          // nonProfit={profile?.nonProfitCompany}
          // company={profile?.company}
          user={profile}
          onClose={() => handleClose()}
          reward={reward}
          post={createdPost}
          isPostPublic={createdPost?.targetViewers === TargetViewers.All}
          isPost
          // isPostPublic // TODO:check - shayam - when you add the public feature use this prop for a public post success screen. Use the same for volunteering/campaign/celebration
        />
      )}
    </Dialog>
  );
}

export default CreatePostDialog;
